import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowRight, ArrowLeft, Calendar, MapPin, Users, Loader2, Check, Crown, Sparkles } from "lucide-react";

const fmtDate = (s) => {
  if (!s) return "";
  try {
    return new Date(s).toLocaleDateString("de-CH", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return s; }
};

const kindBadge = { workshop: "Workshop", retreat: "Retreat", seminar: "Seminar" };

export const EventsList = () => {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/events?upcoming_only=false").then(r => setEvents(r.data));
  }, []);

  return (
    <div className="pt-28 min-h-screen" data-testid="events-page">
      <div className="container-sat pt-8 pb-12">
        <div className="overline mb-4">SAT · Live-Termine</div>
        <h1 className="headline-xl text-5xl md:text-7xl">Workshops · Retreats · Seminare.</h1>
        <p className="serif italic text-xl text-white/55 mt-6 max-w-2xl">Begegne Nenad und der SAT-Community persönlich. Limitierte Plätze, intime Räume.</p>
      </div>

      <section className="py-12 bg-[#050B1B]">
        <div className="container-sat grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="events-grid">
          {events.length === 0 && <div className="col-span-full text-center text-white/45 py-12">Aktuell keine Termine veröffentlicht.</div>}
          {events.map((e) => (
            <Link key={e.id} to={`/events/${e.slug}`} className={`group relative overflow-hidden rounded-2xl border bg-[#0E1A30] transition ${e.is_exclusive ? "border-[#D4AF37]/40 hover:border-[#D4AF37]/70" : "border-white/8 hover:border-white/20"}`} data-testid={`event-card-${e.slug}`}>
              <div className="aspect-[16/10] overflow-hidden relative">
                {e.image_url && <img src={e.image_url} alt="" className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition duration-700" />}
                {e.is_exclusive && (
                  <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#081328]/85 backdrop-blur border border-[#D4AF37]/40 text-[#D4AF37] text-[9px] font-semibold uppercase tracking-[0.18em]">★ Exklusiv</span>
                )}
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between text-xs">
                  <span className="overline">{kindBadge[e.kind] || e.kind}</span>
                  <span className="text-[#D4AF37]">{e.price_chf > 0 ? `CHF ${e.price_chf.toFixed(0)}` : "Auf Anfrage"}</span>
                </div>
                <h3 className="serif text-2xl mt-3 font-light leading-tight">{e.title}</h3>
                <div className="text-xs text-white/50 mt-3 space-y-1">
                  <div className="flex items-center gap-2"><Calendar size={11} className="text-[#D4AF37]" /> {fmtDate(e.starts_at)}{e.ends_at && e.ends_at !== e.starts_at ? ` – ${fmtDate(e.ends_at)}` : ""}</div>
                  <div className="flex items-center gap-2"><MapPin size={11} className="text-[#D4AF37]" /> {e.location}</div>
                  {(() => {
                    const tb = (e.booked || 0) + (e.phantom_booked || 0);
                    const sl = e.capacity ? Math.max(0, e.capacity - tb) : null;
                    if (sl === null) return null;
                    return <div className="flex items-center gap-2"><Users size={11} className="text-[#D4AF37]" /> {sl} {sl === 1 ? "Platz" : "Plätze"} frei</div>;
                  })()}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export const EventDetail = () => {
  const { slug } = useParams();
  const [e, setE] = useState(null);
  const [form, setForm] = useState({ first_name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("register"); // "register" | "waitlist"

  useEffect(() => {
    api.get(`/events/${slug}`).then(r => setE(r.data)).catch(() => setE(undefined));
  }, [slug]);

  const submit = async (ev) => {
    ev.preventDefault();
    setStatus("loading"); setErr("");
    try {
      const endpoint = effectiveMode === "waitlist" ? `/events/${slug}/waitlist` : `/events/${slug}/register`;
      await api.post(endpoint, form);
      setStatus("ok");
    } catch (e) { setErr(e?.response?.data?.detail || "Fehler"); setStatus("err"); }
  };

  if (e === undefined) return <div className="pt-32 text-center text-white/55">Event nicht gefunden</div>;
  if (!e) return <div className="pt-32 text-center text-white/55"><Loader2 className="animate-spin mx-auto" /></div>;

  const totalBooked = (e.booked || 0) + (e.phantom_booked || 0);
  const spotsLeft = e.capacity ? Math.max(0, e.capacity - totalBooked) : null;
  const isExclusive = !!e.is_exclusive || (e.capacity && e.capacity <= 12);
  const isSoldOut = spotsLeft === 0;
  const waitlistCount = e.waitlist_count || 0;
  // Auto-switch to waitlist mode if event is sold out
  const effectiveMode = isSoldOut ? "waitlist" : mode;
  const heroH = isExclusive ? "h-[78vh] md:h-[88vh]" : "h-[55vh]";

  return (
    <div className="pt-24 min-h-screen" data-testid={`event-detail-${slug}`}>
      <div className={`relative ${heroH} overflow-hidden`}>
        {e.hero_video_url ? (
          <video
            src={e.hero_video_url}
            autoPlay muted loop playsInline
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : e.image_url && (
          <img
            src={e.image_url}
            alt=""
            className={`absolute inset-0 w-full h-full object-cover ${isExclusive ? "event-ken-burns opacity-65" : "opacity-50"}`}
          />
        )}
        {/* Atmosphere overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#081328] via-[#081328]/65 to-transparent"></div>
        {isExclusive && (
          <>
            <div className="absolute inset-0 pointer-events-none" style={{
              background: "radial-gradient(circle at 20% 20%, rgba(212,175,55,0.15), transparent 45%), radial-gradient(circle at 85% 75%, rgba(75,46,131,0.18), transparent 45%)"
            }} />
            <div className="absolute inset-0 event-grain pointer-events-none" />
          </>
        )}
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-[1200px] mx-auto px-6 lg:px-12 w-full pb-12">
            <Link to="/events" className="btn-ghost text-sm"><ArrowLeft size={14} /> Alle Termine</Link>
            <div className="flex flex-wrap gap-2 mt-4 mb-3 items-center">
              <span className="overline">{kindBadge[e.kind]}</span>
              {isExclusive && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] font-semibold uppercase tracking-[0.2em]" data-testid="exclusive-badge">
                  <Crown size={11} /> Exklusiv
                </span>
              )}
            </div>
            <h1 className="headline-xl text-4xl md:text-7xl">{e.title}</h1>
            <p className="serif italic text-xl md:text-2xl text-white/65 mt-3 max-w-2xl">{e.subtitle}</p>
          </div>
        </div>
      </div>

      {/* Scarcity strip — only for exclusive */}
      {isExclusive && spotsLeft !== null && (
        <div className={`border-y ${isSoldOut ? "bg-gradient-to-r from-[#1a1d3a] via-[#28244e] to-[#1a1d3a] border-[#7a5cf0]/30" : "bg-gradient-to-r from-[#3b2510] via-[#4d3415] to-[#3b2510] border-[#D4AF37]/30"}`}>
          <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-4 flex flex-col md:flex-row items-center justify-between gap-3" data-testid="scarcity-strip">
            <div className="flex items-center gap-3 text-sm">
              <Sparkles size={16} className={isSoldOut ? "text-[#a48cff]" : "text-[#D4AF37]"} />
              {isSoldOut ? (
                <span className="text-[#E0D7FF]">
                  <b className="text-[#FFFFFF]">Ausgebucht</b> · <b className="text-[#FFFFFF]">{waitlistCount}</b> {waitlistCount === 1 ? "Person" : "Personen"} auf der Warteliste · Wir benachrichtigen sofort bei Freiwerden eines Platzes
                </span>
              ) : (
                <span className="text-[#F6E5A8]">
                  Nur <b className="text-[#FFE9A8]">{e.capacity}</b> Plätze · <b className="text-[#FFE9A8]">{totalBooked}</b> bereits reserviert ·{" "}
                  <span className="text-[#FFE9A8]">{spotsLeft} {spotsLeft === 1 ? "Platz" : "Plätze"} verfügbar</span>
                </span>
              )}
            </div>
            {/* Capacity dots */}
            <div className="flex items-center gap-1.5" aria-label="Spots indicator">
              {Array.from({ length: e.capacity }).map((_, i) => (
                <span key={i}
                  className={`w-2.5 h-2.5 rounded-full transition ${i < totalBooked ? (isSoldOut ? "bg-[#7a5cf0]" : "bg-[#D4AF37]") : "bg-white/15 border border-white/30"}`} />
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-16 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <div className="space-y-2 text-white/75 text-sm">
            <div className="flex items-center gap-3"><Calendar size={14} className="text-[#D4AF37]" /> {fmtDate(e.starts_at)}{e.ends_at && e.ends_at !== e.starts_at ? ` – ${fmtDate(e.ends_at)}` : ""}</div>
            <div className="flex items-center gap-3"><MapPin size={14} className="text-[#D4AF37]" /> {e.location}</div>
            {e.capacity > 0 && <div className="flex items-center gap-3"><Users size={14} className="text-[#D4AF37]" /> {totalBooked} / {e.capacity} angemeldet</div>}
          </div>
          <p className="text-white/85 text-lg leading-relaxed mt-8 whitespace-pre-line dropcap">{e.description}</p>

          {/* Highlights for exclusive */}
          {isExclusive && Array.isArray(e.highlights) && e.highlights.length > 0 && (
            <div className="mt-12" data-testid="event-highlights">
              <div className="overline mb-4">Was inbegriffen ist</div>
              <ul className="space-y-3">
                {e.highlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.025] border border-white/5">
                    <Check size={16} className="text-[#D4AF37] mt-0.5 shrink-0" />
                    <span className="text-white/85">{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="lg:col-span-5">
          <div className={`glass rounded-3xl p-6 md:p-8 sticky top-28 ${isSoldOut ? "border-[#7a5cf0]/40 ring-1 ring-[#7a5cf0]/15" : isExclusive ? "border-[#D4AF37]/40 ring-1 ring-[#D4AF37]/15" : ""}`} data-testid="event-register-card">
            {isSoldOut ? (
              <div className="overline text-[#a48cff] mb-2 flex items-center gap-1.5"><Sparkles size={11} /> Warteliste · Freiplatz-Benachrichtigung</div>
            ) : isExclusive && (
              <div className="overline text-[#D4AF37] mb-2 flex items-center gap-1.5"><Crown size={11} /> Premium-Reservierung</div>
            )}
            <div className="text-3xl serif font-light text-[#D4AF37]">{e.price_chf > 0 ? `CHF ${e.price_chf.toFixed(0)}` : "Auf Anfrage"}</div>
            <div className="text-xs text-white/45 mt-1">{isExclusive ? "All-inclusive · Vollpension" : "Einmalzahlung"}</div>
            {!isSoldOut && isExclusive && spotsLeft !== null && spotsLeft <= 5 && spotsLeft > 0 && (
              <div className="mt-3 text-xs text-[#FFD274] flex items-center gap-1.5">
                <Sparkles size={12} /> Nur noch <b>{spotsLeft}</b> {spotsLeft === 1 ? "Platz" : "Plätze"} verfügbar
              </div>
            )}
            {isSoldOut && (
              <div className="mt-3 p-3 rounded-lg bg-[#7a5cf0]/10 border border-[#7a5cf0]/30 text-xs text-[#E0D7FF] leading-relaxed">
                Dieses Exklusiv-Format ist <b>vollständig ausgebucht</b>. Trag dich für die Warteliste ein — wenn ein Platz frei wird oder die nächste Edition geöffnet wird, hörst du als Erste:r davon.
              </div>
            )}
            {/* Mode switcher (only when not sold out + exclusive) */}
            {!isSoldOut && isExclusive && status !== "ok" && (
              <div className="mt-4 flex gap-1 p-1 bg-white/[0.04] rounded-lg text-xs" data-testid="event-mode-switch">
                <button type="button" onClick={() => setMode("register")}
                  className={`flex-1 py-2 rounded transition ${effectiveMode === "register" ? "bg-[#D4AF37] text-[#081328] font-semibold" : "text-white/70 hover:text-white"}`}
                  data-testid="mode-register">Platz anfragen</button>
                <button type="button" onClick={() => setMode("waitlist")}
                  className={`flex-1 py-2 rounded transition ${effectiveMode === "waitlist" ? "bg-[#7a5cf0] text-white font-semibold" : "text-white/70 hover:text-white"}`}
                  data-testid="mode-waitlist">Auf Warteliste</button>
              </div>
            )}
            {status === "ok" ? (
              <div className="mt-6 text-center py-6" data-testid="event-register-success">
                <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4 ${effectiveMode === "waitlist" ? "bg-[#7a5cf0] text-white" : "bg-[#D4AF37] text-[#081328]"}`}><Check size={24} /></div>
                <p className="serif text-xl">Danke {form.first_name} — {effectiveMode === "waitlist" ? "du bist auf der Warteliste." : "wir melden uns."}</p>
                {effectiveMode === "waitlist" && (
                  <p className="text-xs text-white/55 mt-3 leading-relaxed">Du wirst sofort benachrichtigt, sobald ein Platz frei wird oder die nächste Edition geöffnet wird.</p>
                )}
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3 mt-6" data-testid={effectiveMode === "waitlist" ? "event-waitlist-form" : "event-register-form"}>
                <input required className="sat-input" placeholder="Vorname" value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} />
                <input required type="email" className="sat-input" placeholder="E-Mail" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
                <input className="sat-input" placeholder="Telefon (optional)" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
                <textarea className="sat-input" rows={3}
                  placeholder={effectiveMode === "waitlist" ? "Deine Motivation (optional)" : "Nachricht (optional)"}
                  value={form.message} onChange={e => setForm({...form, message: e.target.value})} />
                <button disabled={status === "loading"}
                  className={`w-full justify-center inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition ${effectiveMode === "waitlist" ? "bg-[#7a5cf0] text-white hover:bg-[#6a4ce0]" : `btn-primary ${isExclusive ? "pulse-gold" : ""}`}`}
                  data-testid={effectiveMode === "waitlist" ? "event-waitlist-submit" : "event-register-submit"}>
                  {status === "loading" ? <Loader2 className="animate-spin" size={16} /> : (
                    <>{effectiveMode === "waitlist" ? "Auf Warteliste setzen" : (isExclusive ? "Platz anfragen" : "Platz reservieren")} <ArrowRight size={14} /></>
                  )}
                </button>
                {err && <p className="text-red-300 text-xs">{err}</p>}
                <p className="text-xs text-white/40 mt-2">
                  {effectiveMode === "waitlist"
                    ? "Du erhältst eine Bestätigung per Mail. Bei Freiwerden eines Platzes melden wir uns innerhalb von 24 Stunden."
                    : (isExclusive ? "Unverbindliche Anfrage — wir prüfen die Verfügbarkeit und melden uns innerhalb von 24 Stunden mit Reservierungsbestätigung." : "Unverbindliche Reservierung — du erhältst die Zahlungsdetails per Mail.")}
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventsList;
